import React, { useState } from "react";
import {
  Button,
  Group,
  Text,
  TextInput,
  Textarea,
  Title,
  NumberInput,
  useMantineColorScheme,
  Container,
} from "@mantine/core";
import { DateTimePicker } from "@mantine/dates";
import { notifications } from "@mantine/notifications";
import { useNavigate } from "react-router-dom";
import { IconArrowLeft, IconCalendar } from "@tabler/icons-react";
import { useCreateSeasonMutation } from "generated/graphql";

/**
 * SeasonCreate
 *
 * Admin page to create a new season.
 * Seasons are created in Draft status and can be published later.
 */
export default function SeasonCreate() {
  const navigate = useNavigate();
  const { colorScheme } = useMantineColorScheme();
  const isDark = colorScheme === "dark";

  const [createSeason, { loading: creating }] = useCreateSeasonMutation();

  // Form state
  const [seasonNumber, setSeasonNumber] = useState<number | "">(1);
  const [displayName, setDisplayName] = useState("");
  const [tag, setTag] = useState("");
  const [description, setDescription] = useState("");
  const [startsAt, setStartsAt] = useState<Date | null>(null);
  const [endsAt, setEndsAt] = useState<Date | null>(null);
  const [visibleFrom, setVisibleFrom] = useState<Date | null>(null);
  const [visibleTo, setVisibleTo] = useState<Date | null>(null);

  const isValid =
    seasonNumber !== "" &&
    displayName.trim() !== "" &&
    tag.trim() !== "" &&
    startsAt !== null &&
    endsAt !== null &&
    endsAt > startsAt;

  async function handleCreate() {
    if (!isValid || !startsAt || !endsAt) return;

    try {
      const result = await createSeason({
        variables: {
          input: {
            seasonNumber: seasonNumber as number,
            displayName: displayName.trim(),
            tag: tag.trim(),
            description: description.trim() || undefined,
            startsAt: startsAt.toISOString(),
            endsAt: endsAt.toISOString(),
            visibleFrom: visibleFrom?.toISOString(),
            visibleTo: visibleTo?.toISOString(),
          },
        },
      });

      if (result.data?.createSeason) {
        notifications.show({
          title: "Success",
          message: `Season "${result.data.createSeason.displayName}" created in Draft status`,
          color: "green",
        });
        navigate(`/admin-hub/seasons/${result.data.createSeason.id}`);
      } else {
        notifications.show({
          title: "Error",
          message: "Failed to create season",
          color: "red",
        });
      }
    } catch (err: any) {
      notifications.show({
        title: "Error",
        message: err?.message || "An error occurred while creating the season",
        color: "red",
      });
    }
  }

  return (
    <Container size="xl" px="lg" py="lg">
      <Group justify="flex-start" mb="sm">
        <Button
          variant="filled"
          color="blue"
          className="bg-blue-600 hover:bg-blue-700 text-white"
          leftSection={<IconArrowLeft size={16} />}
          onClick={() => navigate("/admin-hub/seasons")}
          size="sm"
        >
          Back to Seasons
        </Button>
      </Group>
      <div className="space-y-4 max-w-2xl">
        <Title c={isDark ? "white" : "black"}>Create Season</Title>
        <Text c={isDark ? "gray.4" : "gray.7"}>
          Create a new competitive season. Seasons are created in Draft status and must be published to become visible.
        </Text>

        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <NumberInput
              label="Season Number"
              placeholder="1"
              value={seasonNumber}
              onChange={(val) => setSeasonNumber(val === "" ? "" : Number(val))}
              min={1}
              withAsterisk
            />

            <TextInput
              label="Tag"
              placeholder="S25-1"
              description="Short identifier (used for URL slug)"
              value={tag}
              onChange={(e) => setTag(e.currentTarget.value)}
              withAsterisk
              autoComplete="off"
            />
          </div>

          <TextInput
            label="Display Name"
            placeholder="Spring 2025"
            value={displayName}
            onChange={(e) => setDisplayName(e.currentTarget.value)}
            withAsterisk
            autoComplete="off"
          />

          <Textarea
            label="Description"
            placeholder="Optional description for this season..."
            value={description}
            onChange={(e) => setDescription(e.currentTarget.value)}
            minRows={3}
          />

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <DateTimePicker
              label="Start Date"
              placeholder="Select start date/time"
              value={startsAt}
              onChange={setStartsAt}
              leftSection={<IconCalendar size={16} />}
              withAsterisk
              clearable
            />

            <DateTimePicker
              label="End Date"
              placeholder="Select end date/time"
              value={endsAt}
              onChange={setEndsAt}
              leftSection={<IconCalendar size={16} />}
              withAsterisk
              clearable
              minDate={startsAt || undefined}
            />
          </div>

          {startsAt && endsAt && endsAt <= startsAt && (
            <Text c="red" size="sm">
              End date must be after start date
            </Text>
          )}

          <Text size="sm" fw={500} c={isDark ? "gray.3" : "gray.7"} mt="md">
            Visibility (Optional)
          </Text>
          <Text size="xs" c={isDark ? "gray.5" : "gray.6"}>
            Control when this season appears to users. Leave empty for default behavior.
          </Text>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <DateTimePicker
              label="Visible From"
              placeholder="When to show season"
              value={visibleFrom}
              onChange={setVisibleFrom}
              leftSection={<IconCalendar size={16} />}
              clearable
            />

            <DateTimePicker
              label="Visible Until"
              placeholder="When to hide season"
              value={visibleTo}
              onChange={setVisibleTo}
              leftSection={<IconCalendar size={16} />}
              clearable
            />
          </div>

          <Group mt="xl">
            <Button
              className="bg-blue-500 hover:bg-blue-600"
              onClick={handleCreate}
              disabled={!isValid || creating}
              loading={creating}
            >
              Create Season
            </Button>
            <Button variant="light" color="gray" onClick={() => navigate(-1)}>
              Cancel
            </Button>
          </Group>
        </div>
      </div>
    </Container>
  );
}
